import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";

import welcomeImage from "@/assets/onboarding-welcome.png";
import featuresImage from "@/assets/onboarding-features.png";
import startImage from "@/assets/onboarding-start.png";

const ONBOARDING_KEY = "onboarding_completed";

export function useOnboarding() {
  const isCompleted = localStorage.getItem(ONBOARDING_KEY) === "true";
  
  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
  };

  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_KEY);
  };

  return { isCompleted, completeOnboarding, resetOnboarding };
}

interface OnboardingProps {
  onComplete: () => void;
}

export function Onboarding({ onComplete }: OnboardingProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const { completeOnboarding } = useOnboarding();

  const slides = [
    {
      image: welcomeImage,
      title: t("onboarding.welcome.title"),
      description: t("onboarding.welcome.description"),
    },
    {
      image: featuresImage,
      title: t("onboarding.features.title"),
      description: t("onboarding.features.description"),
    },
    {
      image: startImage,
      title: t("onboarding.start.title"),
      description: t("onboarding.start.description"),
      disclaimer: t("onboarding.start.disclaimer"),
    },
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleComplete = () => {
    completeOnboarding();
    onComplete();
    navigate("/auth");
  };

  const handleSkip = () => {
    completeOnboarding();
    onComplete();
    navigate("/auth");
  };

  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* Full-screen background image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0"
        >
          <img
            src={slides[currentSlide].image}
            alt={slides[currentSlide].title}
            className="w-full h-full object-cover"
          />
        </motion.div>
      </AnimatePresence>

      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />

      {/* Skip button */}
      <div className="absolute top-0 left-0 right-0 flex justify-end p-4 safe-area-top z-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkip}
          className="text-white/80 hover:text-white hover:bg-white/10"
        >
          {t("onboarding.skip")}
        </Button>
      </div>

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end z-10">
        <div className="px-6 pb-8 safe-area-bottom">
          {/* Text content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-center mb-8"
            >
              <h1 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">
                {slides[currentSlide].title}
              </h1>
              <p className="text-white/90 text-lg leading-relaxed drop-shadow-md">
                {slides[currentSlide].description}
              </p>
              {slides[currentSlide].disclaimer && (
                <p className="text-white/60 text-sm mt-4 leading-relaxed drop-shadow-md">
                  {slides[currentSlide].disclaimer}
                </p>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "bg-white w-8"
                    : "bg-white/40 w-2.5 hover:bg-white/60"
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-3">
            {currentSlide > 0 && (
              <Button
                variant="outline"
                size="lg"
                onClick={handlePrev}
                className="flex-shrink-0 border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            
            <Button
              size="lg"
              onClick={handleNext}
              className="flex-1 gap-2 bg-white text-black hover:bg-white/90 font-semibold shadow-lg"
            >
              {isLastSlide ? t("onboarding.getStarted") : t("onboarding.next")}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
